import type { Job_Duration_Stats, Pipeline_Progress, ReportBundle, ReportJob } from "@/lib/parcelq/types";

export const Pipeline_Duration_Defaults_Ms: Record<ReportBundle, number> = {
  amenities: 4 * 60 * 1000,
  feasibility: 6 * 60 * 1000,
  complete: 9 * 60 * 1000,
};

const Duration_Samples_Key = "parcelq-duration-samples";
const Duration_Samples_Max = 12;

type Duration_Samples = Record<ReportBundle, number[]>;

export type Pipeline_Progress_Phase = "queued" | "running" | "loading" | "complete";

function Duration_Samples_Read(): Duration_Samples {
  if (typeof window === "undefined") {
    return { amenities: [], feasibility: [], complete: [] };
  }

  try {
    const Raw = window.localStorage.getItem(Duration_Samples_Key);
    if (!Raw) {
      return { amenities: [], feasibility: [], complete: [] };
    }
    const Parsed = JSON.parse(Raw) as Duration_Samples;
    return {
      amenities: Parsed.amenities ?? [],
      feasibility: Parsed.feasibility ?? [],
      complete: Parsed.complete ?? [],
    };
  } catch {
    return { amenities: [], feasibility: [], complete: [] };
  }
}

function Duration_Samples_Write(Samples: Duration_Samples) {
  if (typeof window === "undefined") {
    return;
  }
  window.localStorage.setItem(Duration_Samples_Key, JSON.stringify(Samples));
}

export function Duration_Avg_Resolve(
  Bundle: ReportBundle,
  Stats: Job_Duration_Stats | null,
): number {
  const Server_Avg =
    Stats?.averages_ms[Bundle] ?? Stats?.averages_ms.all ?? Pipeline_Duration_Defaults_Ms[Bundle];
  const Local = Duration_Samples_Read()[Bundle];

  if (Local.length === 0) {
    return Server_Avg;
  }

  const Local_Avg = Math.round(Local.reduce((Total, Value) => Total + Value, 0) / Local.length);
  if (!Stats?.sample_count) {
    return Local_Avg;
  }

  return Math.round((Server_Avg + Local_Avg) / 2);
}

export function Duration_Sample_Record(Bundle: ReportBundle, Duration_Ms: number) {
  if (Duration_Ms <= 0) {
    return;
  }

  const Samples = Duration_Samples_Read();
  const Next = [Duration_Ms, ...Samples[Bundle]].slice(0, Duration_Samples_Max);
  Duration_Samples_Write({ ...Samples, [Bundle]: Next });
}

export function Job_Duration_Ms_From_Timestamps(Started_At: string, Finished_At: string): number {
  return Math.max(0, new Date(Finished_At).getTime() - new Date(Started_At).getTime());
}

export function Pipeline_Elapsed_Ms_Resolve(Job: ReportJob | null): number {
  if (!Job || Job.status === "queued") {
    return 0;
  }

  const Anchor = Job.running_at || Job.started_at;
  if (!Anchor) {
    return 0;
  }

  return Math.max(0, Date.now() - new Date(Anchor).getTime());
}

function Pipeline_Remaining_Ms_From_Progress(Progress: Pipeline_Progress): number | null {
  const Remaining_Sec = Progress.estimated_remaining_seconds;
  if (typeof Remaining_Sec !== "number" || !Number.isFinite(Remaining_Sec) || Remaining_Sec < 0) {
    return null;
  }

  const Updated_At = Progress.updated_at_unix;
  if (typeof Updated_At === "number" && Updated_At > 0) {
    const Drift_Sec = Math.max(0, Date.now() / 1000 - Updated_At);
    return Math.max(0, (Remaining_Sec - Drift_Sec) * 1000);
  }

  return Remaining_Sec * 1000;
}

function Pipeline_Seconds_Format(Total_Sec: number): { Minutes: number; Seconds: number } {
  const Elapsed_Sec = Math.max(0, Math.floor(Total_Sec));
  return {
    Minutes: Math.floor(Elapsed_Sec / 60),
    Seconds: Elapsed_Sec % 60,
  };
}

function Pipeline_Elapsed_Label_From_Seconds(Elapsed_Sec: number): string {
  const { Minutes, Seconds } = Pipeline_Seconds_Format(Elapsed_Sec);
  if (Minutes === 0) {
    return `${Seconds}s elapsed`;
  }
  return `${Minutes}m ${Seconds}s elapsed`;
}

function Pipeline_Remaining_Label_From_Seconds(Remaining_Sec: number): string | null {
  const Ceiled_Sec = Math.ceil(Remaining_Sec);
  if (Ceiled_Sec <= 0) {
    return "Finishing up…";
  }

  const { Minutes, Seconds } = Pipeline_Seconds_Format(Ceiled_Sec);
  if (Minutes === 0) {
    return `~${Seconds}s remaining`;
  }
  return `~${Minutes}m ${Seconds}s remaining`;
}

function Pipeline_Remaining_Ms_From_Avg(Elapsed_Ms: number, Avg_Duration_Ms: number): number {
  return Math.max(0, Avg_Duration_Ms - Elapsed_Ms);
}

export function Pipeline_Percent_Compute(
  Job: ReportJob | null,
  Avg_Duration_Ms: number,
  Phase: Pipeline_Progress_Phase,
): number {
  if (Phase === "complete") {
    return 100;
  }
  if (Phase === "loading") {
    return 98;
  }

  const Progress = Job?.pipeline_progress;
  if (Progress && typeof Progress.percent === "number") {
    return Math.min(97, Math.max(2, Math.round(Progress.percent)));
  }

  if (Job?.status === "queued") {
    return 2;
  }

  const Elapsed_Ms = Pipeline_Elapsed_Ms_Resolve(Job);
  if (Elapsed_Ms <= 0 || Avg_Duration_Ms <= 0) {
    return 5;
  }

  const Raw = Math.round((Elapsed_Ms / Avg_Duration_Ms) * 100);
  return Math.min(97, Math.max(5, Raw));
}

export function Pipeline_Elapsed_Label(Job: ReportJob | null, Phase: Pipeline_Progress_Phase): string {
  if (Job?.status === "queued" || Phase === "queued") {
    return "Queued…";
  }

  const Elapsed_Sec = Pipeline_Elapsed_Ms_Resolve(Job) / 1000;
  return Pipeline_Elapsed_Label_From_Seconds(Elapsed_Sec);
}

export function Pipeline_Remaining_Label(
  Job: ReportJob | null,
  Avg_Duration_Ms: number,
  Phase: Pipeline_Progress_Phase,
): string {
  if (Phase === "loading") {
    return "Loading dashboard…";
  }
  if (Job?.status === "queued" || Phase === "queued") {
    return "Waiting for server…";
  }

  const Progress = Job?.pipeline_progress;
  const From_Progress = Progress ? Pipeline_Remaining_Ms_From_Progress(Progress) : null;
  const Remaining_Ms =
    From_Progress ?? Pipeline_Remaining_Ms_From_Avg(Pipeline_Elapsed_Ms_Resolve(Job), Avg_Duration_Ms);
  const Remaining_Label = Pipeline_Remaining_Label_From_Seconds(Remaining_Ms / 1000);

  return Remaining_Label ?? "Finishing up…";
}

export function Pipeline_Stage_Label(Job: ReportJob | null): string | null {
  const Progress = Job?.pipeline_progress;
  if (!Progress || Progress.total_steps <= 0) {
    return null;
  }

  const Index = Progress.current_step_label
    ? Math.min(Progress.completed_steps + 1, Progress.total_steps)
    : Math.min(Math.max(Progress.completed_steps, 1), Progress.total_steps);

  return `${Index}/${Progress.total_steps}`;
}

export function Pipeline_Status_Display(
  Job: ReportJob | null,
  Phase: Pipeline_Progress_Phase,
): string {
  if (Phase === "loading") {
    return "Loading";
  }
  if (Job?.status === "queued" || Phase === "queued") {
    return "Queued";
  }

  const Stage = Job?.pipeline_progress?.current_step_label;
  if (Stage) {
    return `Running [${Stage}]`;
  }
  if (Job?.status === "running") {
    return "Running";
  }

  const Status = Job?.status?.trim() || "running";
  return Status.charAt(0).toUpperCase() + Status.slice(1);
}
